import { Injectable } from '@angular/core';

import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { OddsOperations } from '@frontend/sports/odds/feature';
import { PlacementErrorType, ResponseError } from '@frontend/sports/types/betslip';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { SyncUserSettingsService } from '@frontend/sports/user/feature';

import { formatObject } from '../../../base/utils/format-object';
import { getPosError } from '../../../base/utils/pos-errors';
import { BetslipType } from '../../../core/betslip-type';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { PickId } from '../../../core/picks/pick-id';
import { getComboSlipId, getEditBetSlipId, getSingleSlipId, getSystemSlipIdFromString } from '../../bet-generation/services/slip.utils';
import { reconvertSystem } from '../../system-bet/utils/system-type.helper';
import { BetPlacementError } from '../../validation/errors/bet-placement-error';
import { CleanStrategy, ErrorOrigin } from '../../validation/errors/betslip-error';
import { LossLimitExceeded } from '../../validation/errors/br-errors/loss-limit-exceeded';
import { BWIComboPrevention } from '../../validation/errors/bwi/bwi-combo-prevention';
import { BWIMaxWinPerUser } from '../../validation/errors/bwi/bwi-max-win-per-user';
import { BWIMissingInfo } from '../../validation/errors/bwi/bwi-missing-info';
import { BWINotPlayable } from '../../validation/errors/bwi/bwi-not-playable';
import { BWIOutOfBusinessHours } from '../../validation/errors/bwi/bwi-out-of-business-hours';
import { BWIServiceClosed } from '../../validation/errors/bwi/bwi-service-closed';
import { BWISystemStakeNotWholeStake } from '../../validation/errors/bwi/bwi-system-stake-not-whole-stake';
import { LossLimitExceededCalendarWeek } from '../../validation/errors/de-errors/loss-limit-exceeded-calendar-week';
import { LossLimitExceededDaily } from '../../validation/errors/de-errors/loss-limit-exceeded-daily';
import { LossLimitExceededDerivedMonthly } from '../../validation/errors/de-errors/loss-limit-exceeded-derived-monthly';
import { LossLimitExceededMonthly } from '../../validation/errors/de-errors/loss-limit-exceeded-monthly';
import { BetIsAlreadyPlaced } from '../../validation/errors/edit-bet/bet-is-already-placed';
import { InvalidEditBetRequest } from '../../validation/errors/edit-bet/invalid-edit-bet-request';
import { OasisDown } from '../../validation/errors/edit-bet/oasis-down';
import { ParallelGamePlayPrevention } from '../../validation/errors/edit-bet/parallel-Game-play-prevention';
import { PlayerNotRegisteredWithCFR } from '../../validation/errors/edit-bet/player-not-registered-with-CFR';
import { UserIsSelfExcluded } from '../../validation/errors/edit-bet/user-is-self-excluded';
import { AccaBoostError } from '../../validation/errors/general/acca-boost-error';
import { BetPlacementExtensionError } from '../../validation/errors/general/bet-placement-extension-error';
import { BetPlacementNotAllowedForThisCountry } from '../../validation/errors/general/bet-placement-not-allowed-for-this-country';
import { BetTypeNotAllowedInThisApplication } from '../../validation/errors/general/bet-type-not-allowed-in-this-application';
import { DoubleBetPrevention } from '../../validation/errors/general/double-bet-prevention';
import { EdsBetAndGetTokenError } from '../../validation/errors/general/eds-bet-and-get-token-error';
import { InconsistentStakeTax } from '../../validation/errors/general/inconsistent-stake-tax';
import { IncorrectBankerCount } from '../../validation/errors/general/incorrect-banker-count';
import { IncorrectBetCount } from '../../validation/errors/general/incorrect-bet-count';
import { IncorrectMultipleForecastCount } from '../../validation/errors/general/incorrect-multiple-forecast-count';
import { OverMaximumStake } from '../../validation/errors/general/over-maximum-stake';
import { PromoTokenError } from '../../validation/errors/general/promo-token-error';
import { TechnicalError } from '../../validation/errors/general/technical-error';
import { UnderMinimumOddsBoostRewardError } from '../../validation/errors/general/under-minimum-odds-boost-reward-error.model';
import { UnderMinimumRiskFreeCompensationError } from '../../validation/errors/general/under-minimum-risk-free-compensation-error';
import { UnderMinimumStake } from '../../validation/errors/general/under-minimum-stake';
import { UnderMinimumWinnings } from '../../validation/errors/general/under-minimum-winnings';
import { UnknownError } from '../../validation/errors/general/unknown-error';
import { GeoLocationError } from '../../validation/errors/geo-location/geo-location-error';
import { SessionLimitExceeded } from '../../validation/errors/geo-location/session-limit-exceeded';
import { SessionTokenInvalid } from '../../validation/errors/geo-location/session-token-invalid';
import { BlockedNegativeBalance } from '../../validation/errors/on-errors/blocked-negative-balance';
import { CustomerScrubError } from '../../validation/errors/promo-restriction/customer-scrub-error';
import { BlockedAsInsider } from '../../validation/errors/result/blocked-as-insider';
import { ComboPreventionPickError } from '../../validation/errors/result/combo-prevention-pick-error';
import { CutOffDateReached } from '../../validation/errors/result/cut-off-date-reached';
import { DuplicatedPick } from '../../validation/errors/result/duplicated-pick';
import { InvalidOptionOrNoSignature } from '../../validation/errors/result/invalid-option-or-no-signature';
import { MaxBetCombo } from '../../validation/errors/result/max-bet-combo';
import { MaxBetSingle } from '../../validation/errors/result/max-bet-single';
import { MaxWinPerUser } from '../../validation/errors/result/max-win-per-user';
import { MinimumCombo } from '../../validation/errors/result/minimum-combo';
import { MissingInfo } from '../../validation/errors/result/missing-info';
import { NotAllowedInThisApplication } from '../../validation/errors/result/not-allowed-in-this-application';
import { OddsChanged } from '../../validation/errors/result/odds-changed';
import { OpenDateNotReached } from '../../validation/errors/result/open-date-not-reached';
import { PickInvisible } from '../../validation/errors/result/pick-invisible';
import { PickStatusChanged } from '../../validation/errors/result/pick-status-changed';
import { ResultError } from '../../validation/errors/result/result-error';
import { UnderMinimumOdds } from '../../validation/errors/result/under-minimum-odds';
import { USCustomerBlockedInactiveError } from '../../validation/errors/us-errors/customer-blocked-inactive-errors/customer-blocked-inactive-error';
import { USBuyInLimitExceeded } from '../../validation/errors/us-errors/us-buy-in-limit-exceeded';
import { USOverMaxNewJerseyWinPoolLimit } from '../../validation/errors/us-errors/us-over-max-nj-win-pool-limit';
import { USOverMaxUsWinPoolLimit } from '../../validation/errors/us-errors/us-over-max-us-win-pool-limit';
import { AccountClosed } from '../../validation/errors/user/account-closed';
import { BetTypeNotAllowedForFreeBet } from '../../validation/errors/user/bet-type-not-allowed-for-free-bet';
import { BetTypeNotAllowedForOddsBoost } from '../../validation/errors/user/bet-type-not-allowed-for-odds-boost';
import { BetTypeNotAllowedForRiskFree } from '../../validation/errors/user/bet-type-not-allowed-for-risk-free';
import { BettingOfferBonusRestrictedError } from '../../validation/errors/user/betting-offer-bonus-restricted-error';
import { BlockedCoolOff } from '../../validation/errors/user/blocked-cool-off';
import { BlockedResponsibleGamingReasons } from '../../validation/errors/user/blocked-responsible-gaming-reasons';
import { BlockedServiceClosureByAgent } from '../../validation/errors/user/blocked-service-closure-by-agent';
import { BlockedServiceClosureByPlayer } from '../../validation/errors/user/blocked-service-closure-by-player';
import { ElToroLiveMaxBet } from '../../validation/errors/user/el-toro-live-max-bet';
import { FreeBetAlreadyUsed } from '../../validation/errors/user/free-bet-already-used';
import { FreeBetExpired } from '../../validation/errors/user/free-bet-expired';
import { FreeBetNotActive } from '../../validation/errors/user/free-bet-not-active';
import { FreeBetNotAllowedInThisApplication } from '../../validation/errors/user/free-bet-not-allowed-in-this-application';
import { FreeBetNotMatchingBetCount } from '../../validation/errors/user/free-bet-not-matching-bet-count';
import { FreeBetNotMatchingBettingOffer } from '../../validation/errors/user/free-bet-not-matching-betting-offer';
import { FreeBetNotMatchingEvent } from '../../validation/errors/user/free-bet-not-matching-event';
import { FreeBetNotMatchingGameType } from '../../validation/errors/user/free-bet-not-matching-game-type';
import { FreeBetNotMatchingLeague } from '../../validation/errors/user/free-bet-not-matching-league';
import { FreeBetNotMatchingSlipType } from '../../validation/errors/user/free-bet-not-matching-slip-type';
import { FreeBetNotMatchingSport } from '../../validation/errors/user/free-bet-not-matching-sport';
import { FreeBetUnderMinimumOdds } from '../../validation/errors/user/free-bet-under-minimum-odds';
import { GamblingControlMaxBet } from '../../validation/errors/user/gambling-control-max-bet';
import { IncorrectMoneyFormat } from '../../validation/errors/user/incorrect-money-format';
import { InvalidFreeBet } from '../../validation/errors/user/invalid-free-bet';
import { NotEnoughBonusMoney } from '../../validation/errors/user/not-enough-bonus-money';
import { NotEnoughFunMoney } from '../../validation/errors/user/not-enough-fun-money';
import { NotEnoughMoney } from '../../validation/errors/user/not-enough-money';
import { NotWholeStake } from '../../validation/errors/user/not-whole-stake';
import { OgpBetNotAllowedInThisApplication } from '../../validation/errors/user/ogp-bet-not-allowed-in-this-application';
import { OverAskOfferError } from '../../validation/errors/user/over-ask-offer-error';
import { OverMaxBetLimit } from '../../validation/errors/user/over-max-bet-limit';
import { OverMaxBetLimitDaily } from '../../validation/errors/user/over-max-bet-limit-daily';
import { OverMaxBetLimitMonthly } from '../../validation/errors/user/over-max-bet-limit-monthly';
import { OverMaxBetLimitSevenDays } from '../../validation/errors/user/over-max-bet-limit-seven-days';
import { OverMaxBetLimitWeekly } from '../../validation/errors/user/over-max-bet-limit-weekly';
import { OverallMaxBet } from '../../validation/errors/user/overall-max-bet';
import { OverallMaxWinPerUser } from '../../validation/errors/user/overall-max-win-per-user';
import { RegulatoryCountryBlocked } from '../../validation/errors/user/regulatory-country-blocked';
import { UnderMinimumComboSize } from '../../validation/errors/user/under-minimum-combo-size';
import { UnderMinimumCompulsoryOdds } from '../../validation/errors/user/under-minimum-compulsory-odds';
import { UnderMinimumOddsLimit } from '../../validation/errors/user/under-minimum-odds-limit';
import { UserError } from '../../validation/errors/user/user-error';
import { WrongCurrency } from '../../validation/errors/user/wrong-currency';

export enum ErrorType {
    PickError,
    BetError,
}

@Injectable()
export class PlaceBetErrorMapper {
    private logger: SportsRemoteLogger;

    private shouldSkipMinimumStakeValidation: boolean;

    private readonly expectedMinimumOddsRegExp = new RegExp('{{ExpectedMinimumOdds}}', 'g');

    constructor(loggerFactory: LoggerFactory, userSettings: SyncUserSettingsService) {
        this.logger = loggerFactory.getLogger('BetslipErrorMapper');

        // In case of PL Taxation we need to skip the client validation because the stake is getting taxed on server side
        // We do not want to block placing a bet, so changing the validator to deduct the tax is not an option
        // If we do not skip validation, this error will not show up because the validator will claim it is already fixed
        this.shouldSkipMinimumStakeValidation = userSettings.isTurnoverTaxApplied;
    }

    private createError(error: ResponseError, pick: BetslipPick | null): BetPlacementError {
        switch (error.type) {
            case PlacementErrorType.AccaBoostError:
            case PlacementErrorType.AccaBoostBelowMinSelectionOdds:
            case PlacementErrorType.AccaBoostDateRangeError:
                return new AccaBoostError();
            case PlacementErrorType.AccountClosed:
                return new AccountClosed();
            case PlacementErrorType.BetPlacementExtensionError:
                return new BetPlacementExtensionError();
            case PlacementErrorType.BetTypeNotAllowed:
                return new BetTypeNotAllowedInThisApplication();
            case PlacementErrorType.BetTypeNotAllowedForFreeBet:
                return new BetTypeNotAllowedForFreeBet();
            case PlacementErrorType.BetTypeNotAllowedForOddsBoost:
                return new BetTypeNotAllowedForOddsBoost();
            case PlacementErrorType.BetTypeNotAllowedForRiskFree:
                return new BetTypeNotAllowedForRiskFree();
            case PlacementErrorType.BlockedAsInsider: {
                if (pick === null) {
                    throw new Error(`Can not find pick for error of type ${error.type}`);
                }

                error.translatedErrorMessage = error.translatedErrorMessage
                    ? formatObject(error.translatedErrorMessage, {
                          SportName: pick.sportName.name,
                          RegionName: pick.regionName.name,
                          LeagueName: pick.competitionName.name,
                      })
                    : error.translatedErrorMessage;

                return new BlockedAsInsider(pick.id.toString(), error.sportId, error.leagueId, error.regionId);
            }
            case PlacementErrorType.BlockedCoolOff:
                return new BlockedCoolOff(error.date);
            case PlacementErrorType.BlockedCountry:
                return new BetPlacementNotAllowedForThisCountry();
            case PlacementErrorType.BlockedResponsibleGamingReasons:
                return new BlockedResponsibleGamingReasons();
            case PlacementErrorType.BlockedServiceClosureByAgent:
                return new BlockedServiceClosureByAgent();
            case PlacementErrorType.BlockedServiceClosureByPlayer:
                return new BlockedServiceClosureByPlayer();
            case PlacementErrorType.BwiComboPrevention:
                return new BWIComboPrevention(error.errorCode);
            case PlacementErrorType.ChangedOptionStatus: {
                if (pick === null) {
                    throw new Error(`Can not find pick for error of type ${error.type}`);
                }

                return new PickStatusChanged(pick.id.toString(), error.optionStatus);
            }
            case PlacementErrorType.ComboPrevention: {
                if (pick === null) {
                    throw new Error(`Can not find pick for error of type ${error.type}`);
                }

                return new ComboPreventionPickError(pick.id.toString(), error.comboPreventionReason);
            }
            case PlacementErrorType.CutOffDateReached: {
                if (pick === null) {
                    throw new Error(`Can not find pick for error of type ${error.type}`);
                }

                return new CutOffDateReached(pick.id.toString(), error.date);
            }
            case PlacementErrorType.DoubleBetPrevention:
                return new DoubleBetPrevention();
            case PlacementErrorType.DuplicatedOption: {
                if (pick === null) {
                    throw new Error(`Can not find pick for error of type ${error.type}`);
                }

                return new DuplicatedPick(pick.id.toString());
            }
            case PlacementErrorType.EdsBetAndGetError:
                return new EdsBetAndGetTokenError();
            case PlacementErrorType.EdsBetAndGetUnderMinimumBetOddsError:
                return new EdsBetAndGetTokenError();
            case PlacementErrorType.EdsBetAndGetUnderMinimumTotalOddsError:
                return new EdsBetAndGetTokenError();
            case PlacementErrorType.PromoTokenError:
            case PlacementErrorType.PromoTokenOverMaximumTotalOddsError:
            case PlacementErrorType.PromoTokenOverMaximumBetOddsError:
            case PlacementErrorType.PromoTokenMinimumBetOddsError:
            case PlacementErrorType.PromoTokenUnderMinimumTotalOddsError:
                return new PromoTokenError();
            case PlacementErrorType.FreeBetAlreadyUsed:
                return new FreeBetAlreadyUsed(error.freeBet);
            case PlacementErrorType.FreeBetExpired:
                return new FreeBetExpired(error.freeBet);
            case PlacementErrorType.FreeBetNotActive:
                return new FreeBetNotActive(error.freeBet);
            case PlacementErrorType.FreeBetNotAllowedInThisApplication:
                return new FreeBetNotAllowedInThisApplication();
            case PlacementErrorType.FreeBetNotMatchingLeague:
                return new FreeBetNotMatchingLeague(error.freeBet);
            case PlacementErrorType.FreeBetNotMatchingSport:
                return new FreeBetNotMatchingSport(error.freeBet);
            case PlacementErrorType.FreeBetNotMatchingEvent:
                return new FreeBetNotMatchingEvent(error.freeBet);
            case PlacementErrorType.FreeBetNotMatchingBettingOffer:
                return new FreeBetNotMatchingBettingOffer(error.freeBet);
            case PlacementErrorType.FreeBetNotMatchingSlipType:
                return new FreeBetNotMatchingSlipType(error.freeBet);
            case PlacementErrorType.FreeBetNotMatchingGameType:
                return new FreeBetNotMatchingGameType(error.freeBet);
            case PlacementErrorType.FreeBetUnderMinimumOdds:
                return new FreeBetUnderMinimumOdds(error.freeBet);
            case PlacementErrorType.FreeBetNotMatchingBetCount:
                return new FreeBetNotMatchingBetCount(error.freeBet);
            case PlacementErrorType.IncorrectBankerCount:
                return new IncorrectBankerCount();
            case PlacementErrorType.IncorrectOptionCount:
                return new IncorrectBetCount(error.expectedOptions, error.receivedOptions);
            case PlacementErrorType.InconsistentStakeTax:
                return new InconsistentStakeTax();
            case PlacementErrorType.InsufficientBonusMoney:
                return new NotEnoughBonusMoney(error.userMoney || 0);
            case PlacementErrorType.InsufficientBonusMoneyZeroBalance:
                return new NotEnoughBonusMoney(0);
            case PlacementErrorType.InsufficientFunMoney:
                return new NotEnoughFunMoney(error.userMoney || 0);
            case PlacementErrorType.InsufficientMoney:
                return new NotEnoughMoney(error.userMoney || 0);
            case PlacementErrorType.InsufficientMoneyZeroBalance:
            case PlacementErrorType.BlockedPlayMoney:
                return new NotEnoughMoney(0);
            case PlacementErrorType.InvalidCurrency:
                return new WrongCurrency(error.userCurrency, error.betCurrency);
            case PlacementErrorType.InvalidFreeBet:
                return new InvalidFreeBet(error.freeBet);
            case PlacementErrorType.InvalidOption: {
                if (pick === null) {
                    throw new Error(`Can not find pick for error of type ${error.type}`);
                }

                return new MissingInfo(pick.id.toString());
            }
            case PlacementErrorType.InvalidOrNoSignature: {
                if (pick === null) {
                    throw new Error(`Can not find pick for error of type ${error.type}`);
                }

                return new InvalidOptionOrNoSignature(pick.id.toString());
            }
            case PlacementErrorType.LiveMaxBetExceededNoEvent:
            case PlacementErrorType.LiveMaxBetExceeded:
                return new ElToroLiveMaxBet(error.limitPoolSize, error.eventId);
            case PlacementErrorType.MaxWinPerUserExceeded:
                return new BWIMaxWinPerUser(error.errorCode);
            case PlacementErrorType.MaximumStakeForComboBetsExceeded: {
                if (pick === null) {
                    throw new Error(`Can not find pick for error of type ${error.type}`);
                }

                return new MaxBetCombo(pick.id.toString(), error.betLimit, error.totalStakePlaced);
            }
            case PlacementErrorType.MaximumStakeForSingleBetsExceeded: {
                if (pick === null) {
                    throw new Error(`Can not find pick for error of type ${error.type}`);
                }

                return new MaxBetSingle(pick.id.toString(), error.betLimit, error.totalStakePlaced);
            }
            case PlacementErrorType.MaximumWinOfBetExceeded: {
                if (pick === null) {
                    throw new Error(`Can not find pick for error of type ${error.type}`);
                }

                return new MaxWinPerUser(pick.id.toString(), error.allowedComboWin, error.possibleComboWin);
            }
            case PlacementErrorType.MinimumCombo: {
                if (pick === null) {
                    throw new Error(`Can not find pick for error of type ${error.type}`);
                }

                return new MinimumCombo(pick.id.toString(), error.expectedOptions, error.receivedOptions);
            }
            case PlacementErrorType.MissingInfo:
                return new BWIMissingInfo(error.errorCode, error.optionId);
            case PlacementErrorType.NotAllowedInThisApplication: {
                if (pick === null) {
                    throw new Error(`Can not find pick for error of type ${error.type}`);
                }

                return new NotAllowedInThisApplication(pick.id.toString());
            }
            case PlacementErrorType.NotPlayable:
                return new BWINotPlayable(error.errorCode);
            case PlacementErrorType.NotWholeStake:
                return new NotWholeStake();
            case PlacementErrorType.OddsChanged: {
                if (pick === null) {
                    throw new Error(`Can not find pick for error of type ${error.type}`);
                }

                return new OddsChanged(
                    pick.id.toString(),
                    OddsOperations.createOdds2(error.currentOddsInUserFormat, error.oddsFormat),
                    OddsOperations.createOdds2(error.expectedOddsInUserFormat, error.oddsFormat),
                    OddsOperations.convertOddsFormat(error.oddsFormat),
                );
            }
            case PlacementErrorType.OpenDateNotReached: {
                if (pick === null) {
                    throw new Error(`Can not find pick for error of type ${error.type}`);
                }

                return new OpenDateNotReached(pick.id.toString(), error.date);
            }
            case PlacementErrorType.OptionError: {
                if (pick === null) {
                    throw new Error(`Can not find pick for error of type ${error.type}`);
                }

                return new ResultError(pick.id.toString());
            }
            case PlacementErrorType.OptionInvisible: {
                if (pick === null) {
                    throw new Error(`Can not find pick for error of type ${error.type}`);
                }

                return new PickInvisible(pick.id.toString());
            }
            case PlacementErrorType.OutOfBusinessHours:
                return new BWIOutOfBusinessHours(error.errorCode);
            case PlacementErrorType.OverMaxBetLimit:
                return new OverMaxBetLimit(error.betLimit, error.totalStakePlaced);
            case PlacementErrorType.OverMaxBetLimitDaily:
                return new OverMaxBetLimitDaily(error.betLimit, error.totalStakePlaced);
            case PlacementErrorType.OverMaxBetLimitWeekly:
                return new OverMaxBetLimitWeekly(error.betLimit, error.totalStakePlaced);
            case PlacementErrorType.OverMaxBetLimitSevenDays:
                return new OverMaxBetLimitSevenDays(error.betLimit, error.totalStakePlaced);
            case PlacementErrorType.OverMaxBetLimitMonthly:
                return new OverMaxBetLimitMonthly(error.betLimit, error.totalStakePlaced);
            case PlacementErrorType.GamblingControlMaxBet:
                return new GamblingControlMaxBet(error.bettingLimit, error.totalStakePlayed);
            case PlacementErrorType.OverallMaxBet:
                return new OverallMaxBet(error.bettingLimit, error.totalStakePlayed);
            case PlacementErrorType.OverallMaxWinPerUserExceeded:
                return new OverallMaxWinPerUser(error.comboWin, error.maxComboWin);
            case PlacementErrorType.RegulatoryCountryBlocked:
                return new RegulatoryCountryBlocked();
            case PlacementErrorType.ServiceClosed:
                return new BWIServiceClosed(error.errorCode);
            case PlacementErrorType.StakeAboveMaximumLimitWithinTimeRange:
                return new OverMaximumStake();
            case PlacementErrorType.StakeBelowMinimumLimit: {
                const underMiminimumStakeError = new UnderMinimumStake();

                if (this.shouldSkipMinimumStakeValidation) {
                    underMiminimumStakeError.hasClientValidation = false;
                }

                return underMiminimumStakeError;
            }
            case PlacementErrorType.SystemStakeNotMultipleOfQuarter:
                return new BWISystemStakeNotWholeStake(error.errorCode);
            case PlacementErrorType.TechnicalError:
                return new TechnicalError();
            case PlacementErrorType.UnderMinimumComboSize:
                return new UnderMinimumComboSize(error.comboSizeLimit, error.numberOfPicks);
            case PlacementErrorType.UnderMinimumCompulsoryOdds:
                return new UnderMinimumCompulsoryOdds(error.minimumOdds, error.totalOdds);
            case PlacementErrorType.UnderMinimumOdds: {
                if (pick === null) {
                    throw new Error(`Can not find pick for error of type ${error.type}`);
                }

                return new UnderMinimumOdds(
                    pick.id.toString(),
                    OddsOperations.createOdds2(error.currentOddsInUserFormat, error.oddsFormat),
                    OddsOperations.createOdds2(error.expectedOddsInUserFormat, error.oddsFormat),
                    OddsOperations.convertOddsFormat(error.oddsFormat),
                );
            }
            case PlacementErrorType.UnderMinimumOddsLimit:
                return new UnderMinimumOddsLimit(
                    OddsOperations.createOdds({ odds: error.currentOddsDecimal }),
                    OddsDisplayFormat.EU,
                    error.oddsLimit,
                );
            case PlacementErrorType.UnderMinimumWinnings: {
                return new UnderMinimumWinnings();
            }
            case PlacementErrorType.Unknown:
                return new UnknownError();
            case PlacementErrorType.UserError:
                return new UserError();
            case PlacementErrorType.WrongCurrency:
                return new WrongCurrency(error.userCurrency, error.betCurrency);
            case PlacementErrorType.IncorrectMoneyFormat:
                return new IncorrectMoneyFormat(error.requested);
            case PlacementErrorType.IncorrectMultipleForecastCount:
                return new IncorrectMultipleForecastCount();
            case PlacementErrorType.BetIsAlreadyPlaced:
                return new BetIsAlreadyPlaced();
            case PlacementErrorType.InvalidEditBetRequest:
                return new InvalidEditBetRequest();

            // Ontario related error
            case PlacementErrorType.BlockedNegativeBalance:
                return new BlockedNegativeBalance();

            // BR related errors
            case PlacementErrorType.LossLimitExceeded:
                return new LossLimitExceeded();

            // US related errors
            case PlacementErrorType.BuyInLimitExceeded:
                return new USBuyInLimitExceeded();
            case PlacementErrorType.OverMaxNewJerseyWinPoolLimit:
                return new USOverMaxNewJerseyWinPoolLimit();
            case PlacementErrorType.OverMaxUsWinPoolLimit:
                return new USOverMaxUsWinPoolLimit();
            case PlacementErrorType.SessionLimitExceeded:
                return new SessionLimitExceeded();
            case PlacementErrorType.SessionTokenInvalid:
                return new SessionTokenInvalid();
            case PlacementErrorType.CustomerBlockedInactiveError:
                return new USCustomerBlockedInactiveError();
            case PlacementErrorType.geoLocationError:
                return new GeoLocationError();

            case PlacementErrorType.LossLimitExceededDaily:
                return new LossLimitExceededDaily();
            case PlacementErrorType.LossLimitExceededCalendarWeek:
                return new LossLimitExceededCalendarWeek();
            case PlacementErrorType.LossLimitExceededMonthly:
                return new LossLimitExceededMonthly();
            case PlacementErrorType.LossLimitExceededDerivedMonthly:
                return new LossLimitExceededDerivedMonthly();
            case PlacementErrorType.OverAskOfferError:
                return new OverAskOfferError(error.reason, error.newStakeHint!);

            case PlacementErrorType.customerScrubError:
                return new CustomerScrubError();
            case PlacementErrorType.ParallelGamePlayPrevention:
                return new ParallelGamePlayPrevention();
            case PlacementErrorType.UserIsSelfExcluded:
                return new UserIsSelfExcluded();
            case PlacementErrorType.OasisDown:
                return new OasisDown();
            case PlacementErrorType.PlayerNotRegisteredWithCFR:
                return new PlayerNotRegisteredWithCFR();
            case PlacementErrorType.BettingOfferBonusRestrictedError:
                return new BettingOfferBonusRestrictedError();
            case PlacementErrorType.UnderMinimumOddsBoostReward:
                return new UnderMinimumOddsBoostRewardError();
            case PlacementErrorType.UnderMinimumRiskFreeCompensation:
                return new UnderMinimumRiskFreeCompensationError();
            case PlacementErrorType.OgpBetNotAllowedInThisApplication:
                return new OgpBetNotAllowedInThisApplication();

            default:
                return new UnknownError();
        }
    }

    // getErrorType(error: ResponseError): ErrorType {
    // 	const pickErrors = [
    // 		PlacementErrorType.BlockedAsInsider,
    // 		PlacementErrorType.ChangedOptionStatus,
    // 		PlacementErrorType.ComboPrevention,
    // 		PlacementErrorType.CutOffDateReached,
    // 		PlacementErrorType.DuplicatedOption,
    // 		PlacementErrorType.InvalidOption,
    // 		PlacementErrorType.InvalidOrNoSignature,
    // 		PlacementErrorType.MaximumStakeForComboBetsExceeded,
    // 		PlacementErrorType.MaximumStakeForSingleBetsExceeded,
    // 		PlacementErrorType.MaximumWinOfBetExceeded,
    // 		PlacementErrorType.MaximumWinOfComboBetExceeded,
    // 		PlacementErrorType.MinimumCombo,
    // 		PlacementErrorType.NotAllowedInThisApplication,
    // 		PlacementErrorType.OddsChanged,
    // 		PlacementErrorType.OpenDateNotReached,
    // 		PlacementErrorType.OptionError,
    // 		PlacementErrorType.OptionInvisible,
    // 		PlacementErrorType.UnderMinimumOdds,
    // 	];
    // 	if (pickErrors.some(pe => error.type === pe)) {
    // 		return ErrorType.PickError;
    // 	}
    //
    // 	return ErrorType.BetError;
    // }

    map(error: ResponseError, pick: BetslipPick | null): BetPlacementError {
        try {
            const e = this.createError(error, pick);
            e.newStakeHint = error.newStakeHint;
            e.type = error.type;
            e.errorMessage = this.replaceMinimumOrTotalOdds(error.translatedErrorMessage, error.expectedMinimumOdds) || '';
            e.origin = ErrorOrigin.BetPlacement;
            e.cleanStrategy = CleanStrategy.Manually;
            e.hasClientValidation = false;
            e.detailedErrorCode = error.detailedErrorCode;

            if (!pick) {
                e.platformError = getPosError(error);
            }

            return e;
        } catch (e: any) {
            this.logger.error(`Can not map error: ${error.type} exception:` + e.message);

            return new UnknownError();
        }
    }

    getSlipId(betSlipType: BetslipType, pickId: PickId, systemType: string): string {
        switch (betSlipType) {
            case BetslipType.Single:
                return getSingleSlipId(pickId);
            case BetslipType.Combo:
                return getComboSlipId();
            case BetslipType.System:
                return getSystemSlipIdFromString(reconvertSystem(systemType));
            case BetslipType.EditBet:
                return getEditBetSlipId();
            default:
                return '';
        }
    }

    private replaceMinimumOrTotalOdds(translatedErrorMessage: string, expectedMinimumOdds?: string): string {
        return translatedErrorMessage?.replace(this.expectedMinimumOddsRegExp, expectedMinimumOdds ?? '');
    }
}
